import { useEffect, useState } from "preact/hooks";
import { parseNumber } from "../app/format";
import {
  DENSITY_CLASSES, PROCESS_CLASSES, ROAST_LEVELS, processClassFrom,
  type Bean, type DensityClass, type ProcessClass, type RoastLevel,
} from "../engine/model";
import { hasContent, proxyConfigured, standardProvider, type BeanDraft, type BeanInfoProvider } from "../import";
import { Modal } from "./dialogs";

/**
 * Add or edit a bean. In add mode the product-link field calls the BeanInfoProvider through the CORS proxy and
 * prefills the form. Attribute pickers (roast, process, density, decaf) feed the starting-point prior; the optional
 * per-bean target time replaces the recipe default for this bean everywhere. In edit mode (`initial` given) every
 * field is prefilled and the saved bean keeps its id, archived flag and import metadata.
 */
const ROAST_LABELS: Record<RoastLevel, string> = {
  light: "Light", medium_light: "Medium-light", medium: "Medium", medium_dark: "Medium-dark", dark: "Dark", unknown: "Unknown",
};
const PROCESS_LABELS: Record<ProcessClass, string> = {
  washed: "Washed", honey: "Honey", natural: "Natural", anaerobic: "Anaerobic", unknown: "Unknown",
};
const DENSITY_LABELS: Record<DensityClass, string> = {
  high: "High (dense, high-grown: Geisha, SL28, high-altitude Bourbon)", standard: "Standard", low: "Low (low-grown, Robusta blends)", unknown: "Unknown",
};

/** Map free-text process from an import to the structured class when it is obvious. */
function guessProcessClass(text: string | null): ProcessClass {
  const t = (text ?? "").toLowerCase();
  if (/anaerob|carbonic/.test(t)) return "anaerobic";
  if (/honey|pulped/.test(t)) return "honey";
  if (/natural|dry/.test(t)) return "natural";
  if (/washed|wet/.test(t)) return "washed";
  return processClassFrom(t);
}

export function AddBeanDialog(props: {
  open: boolean; today: string; onClose: () => void; onSave: (bean: Bean) => void;
  provider?: BeanInfoProvider; initial?: Bean | null;
}) {
  const editing = props.initial ?? null;
  const [link, setLink] = useState("");
  const [name, setName] = useState("");
  const [roaster, setRoaster] = useState("");
  const [roastDate, setRoastDate] = useState(props.today);
  const [freezeDate, setFreezeDate] = useState("");
  const [roastLevel, setRoastLevel] = useState<RoastLevel>("unknown");
  const [processClass, setProcessClass] = useState<ProcessClass>("unknown");
  const [densityClass, setDensityClass] = useState<DensityClass>("unknown");
  const [decaf, setDecaf] = useState(false);
  const [targetText, setTargetText] = useState("");
  const [status, setStatus] = useState("");
  const [statusKind, setStatusKind] = useState<"error" | "info">("error");
  const [draft, setDraft] = useState<BeanDraft | null>(null);
  const [fetching, setFetching] = useState(false);

  const reset = () => {
    setLink(editing?.sourceUrl ?? ""); setName(editing?.name ?? ""); setRoaster(editing?.roaster ?? "");
    setRoastDate(editing?.roastDate ?? props.today); setFreezeDate(editing?.freezeDate ?? "");
    setRoastLevel(editing?.roastLevel ?? "unknown"); setProcessClass(editing?.processClass ?? "unknown");
    setDensityClass(editing?.densityClass ?? "unknown"); setDecaf(editing?.decaf ?? false);
    setTargetText(editing?.targetTimeS === null || editing?.targetTimeS === undefined ? "" : String(editing.targetTimeS));
    setStatus(""); setDraft(null); setFetching(false);
  };
  // Load the initial bean each time the dialog opens (add → blank, edit → the bean's values).
  useEffect(() => { if (props.open) reset(); }, [props.open, editing?.id]);

  const close = () => { props.onClose(); };
  const say = (kind: "error" | "info", text: string) => { setStatusKind(kind); setStatus(text); };

  const fetchLink = async () => {
    const url = link.trim();
    if (!url) return;
    if (!proxyConfigured() && !props.provider) {
      say("error", "Link import needs the proxy set up (VITE_PROXY_URL). Fill the form in by hand for now.");
      return;
    }
    setFetching(true);
    try {
      const provider = props.provider ?? standardProvider();
      const d = await provider.fetch(url);
      if (!hasContent(d)) {
        say("error", "Nothing could be read from that link; fill the form in by hand.");
        return;
      }
      setDraft(d);
      if (d.name) setName(d.name);
      if (d.roaster) setRoaster(d.roaster);
      const guessed = guessProcessClass(d.process);
      if (guessed !== "unknown") setProcessClass(guessed);
      if (/decaf/i.test(`${d.name ?? ""} ${d.description ?? ""}`)) setDecaf(true);
      const found = [d.name && "name", d.roaster && "roaster", d.variety && "variety", d.region && "region",
        d.process && "process", d.labelImageUrl && "label image"].filter(Boolean).join(", ");
      say("info", `Prefilled ${found}. Check and edit before saving.`);
    } finally {
      setFetching(false);
    }
  };

  const save = () => {
    const n = name.trim();
    if (!n) { say("error", "Name is required"); return; }
    if (!roastDate) { say("error", "Roast date is required"); return; }
    if (freezeDate && freezeDate < roastDate) { say("error", "Frozen-on date cannot be before the roast date"); return; }
    let target: number | null = null;
    if (targetText.trim() !== "") {
      target = parseNumber(targetText);
      if (target === null || target <= 0) { say("error", "Target time must be a positive number of seconds"); return; }
    }
    props.onSave({
      id: editing?.id ?? null, name: n, roaster: roaster.trim() || null, roastDate, freezeDate: freezeDate || null,
      archived: editing?.archived ?? false,
      variety: draft?.variety ?? editing?.variety ?? null, region: draft?.region ?? editing?.region ?? null,
      process: draft?.process ?? editing?.process ?? null, producer: draft?.producer ?? editing?.producer ?? null,
      sourceUrl: draft?.sourceUrl ?? (link.trim() || editing?.sourceUrl || null),
      labelImagePath: draft?.labelImageUrl ?? editing?.labelImagePath ?? null,
      roastLevel, processClass, densityClass, decaf, targetTimeS: target,
    });
  };

  const metaRows: [string, string][] = [];
  const meta = draft ?? editing;
  if (meta) {
    if (meta.variety) metaRows.push(["Variety", meta.variety]);
    if (meta.region) metaRows.push(["Region", meta.region]);
    if (meta.process) metaRows.push(["Process", meta.process]);
    if (meta.producer) metaRows.push(["Producer", meta.producer]);
  }
  const labelImage = draft?.labelImageUrl ?? editing?.labelImagePath ?? null;

  const picker = <T extends string>(label: string, value: T, options: readonly T[], labels: Record<T, string>, set: (v: T) => void) => (
    <label class="field">{label}
      <select class="pill" value={value} onChange={(e) => set((e.currentTarget as HTMLSelectElement).value as T)}>
        {options.map((o) => <option key={o} value={o}>{labels[o]}</option>)}
      </select>
    </label>
  );

  return (
    <Modal open={props.open} onClose={close}>
      <h2>{editing ? "Edit bean" : "Add bean"}</h2>
      {!editing && (
        <div class="row">
          <input class="grow" type="url" inputMode="url" placeholder="Product link (optional)" value={link}
                 onInput={(e) => setLink((e.currentTarget as HTMLInputElement).value)} />
          <button class="secondary" type="button" onClick={() => void fetchLink()} disabled={fetching || !link.trim()}>
            {fetching ? "…" : "Fetch"}
          </button>
        </div>
      )}
      <label class="field">Name
        <input type="text" value={name} onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)} />
      </label>
      <label class="field">Roaster
        <input type="text" value={roaster} onInput={(e) => setRoaster((e.currentTarget as HTMLInputElement).value)} />
      </label>
      <div class="grid2 stack-narrow">
        <label class="field">Roast date
          <input type="date" value={roastDate} onInput={(e) => setRoastDate((e.currentTarget as HTMLInputElement).value)} />
        </label>
        <label class="field">Frozen on (optional)
          <input type="date" value={freezeDate} onInput={(e) => setFreezeDate((e.currentTarget as HTMLInputElement).value)} />
        </label>
      </div>
      {picker("Roast level", roastLevel, ROAST_LEVELS, ROAST_LABELS, setRoastLevel)}
      {picker("Process", processClass, PROCESS_CLASSES, PROCESS_LABELS, setProcessClass)}
      {picker("Density", densityClass, DENSITY_CLASSES, DENSITY_LABELS, setDensityClass)}
      <label class="row" style={{ fontSize: 14, color: "var(--ink-2)", fontWeight: 500 }}>
        <input type="checkbox" checked={decaf} onChange={(e) => setDecaf((e.currentTarget as HTMLInputElement).checked)}
               style={{ width: 20, height: 20, minHeight: 0, margin: 0 }} />
        Decaf
      </label>
      <label class="field">Target shot time (s, optional)
        <input type="text" inputMode="decimal" placeholder="27.5" value={targetText}
               onInput={(e) => setTargetText((e.currentTarget as HTMLInputElement).value)} />
        <span class="hint">Light roasts are often pulled 28–32 s, dark roasts 23–27 s. Blank uses 27.5 s.</span>
      </label>
      {metaRows.length > 0 && (
        <div class="meta">
          {metaRows.map(([k, v]) => (<><span class="k">{k}</span><span>{v}</span></>))}
        </div>
      )}
      {labelImage && (
        <img src={labelImage} alt="Label" style={{ maxHeight: 96, maxWidth: "100%", borderRadius: 8, objectFit: "contain" }} />
      )}
      {status && <div class={statusKind === "error" ? "error" : "hint"}>{status}</div>}
      <div class="actions">
        <button class="secondary" type="button" onClick={close}>Cancel</button>
        <button type="button" onClick={save}>{editing ? "Save changes" : "Save"}</button>
      </div>
    </Modal>
  );
}
